<template>
  <DataTable
    :table-key="'vehicle-listings-table'"
    :columns="tableColumns"
    :rows="listings"
    :loading="loading"
    :title="title"
    :searchable="searchable"
    :downloadable="false"
    :refreshable="true"
    :customizable="false"
    :filterable="false"
    :server-side="false"
    @refresh-rows="
      e => {
        $emit('refresh-rows', e);
      }
    "
    hide-bottom
    fullscreen
    :pagination="{
      sortBy: defaultSortField,
      descending: defaultSortDirection === 'desc',
      page: 1,
      rowsPerPage: 10000
    }"
    row-key="product_id"
    clickable-rows
    @row-clicked="
      row => {
        handleClickedVehicle(row);
      }
    "
  >
    <template v-slot:title>
      <slot name="title" />
    </template>
    <template v-slot:vehicle_overview="row">
      <div class="flex items-center no-wrap">
        <div class="relative-position">
          <VehicleWatchlistButton
            :product-id="row.product_id"
            style="position: absolute; bottom: 8px; right: 8px; z-index: 1; background: #13141594;"
            dense
          />

          <ImageCarousel
            :images="[row.images[0]]"
            :ratio="4 / 3"
            chevron-size="28px"
            class="--rounded --shadow__bottom"
            style="width: 120px;"
          />
        </div>
        <div class="q-ml-md ellipsis">
          <h6 class="q-mb-none ellipsis">{{ row.title }}</h6>
          <p class="text-text-light ellipsis" style="font-weight: 400">
            {{ row.trim }}
          </p>
        </div>
      </div>
    </template>

    <template v-slot:price_with_drop="row">
      <div class="flex items-center no-wrap">
        <div class="--text-md">{{ row.price_formatted }}</div>
        <PriceDropLabel
          v-if="row.tags.filter(tag => tag.tag === 'Price Drop').length > 0"
          :amount-change="
            row.tags.filter(tag => tag.tag === 'Price Drop')[0].metadata
              .amount_change
          "
          horizontal
          style="margin-left: 6px;"
        />
      </div>
    </template>

    <template v-slot:deal_score="row">
      <div style="width: min-content">
        <ScoreBadge :score="row.deal_score" />
        <q-tooltip
          anchor="center right"
          self="center left"
          max-width="300px"
          :offset="[6, 6]"
          persistent
          style="font-size: 14px"
        >
          Deal Score is<span class="text-weight-medium q-ml-xs"
            >{{ row.deal_score }}/100</span
          >.
        </q-tooltip>
      </div>
    </template>

    <template v-slot:latest_price_change="row">
      <div
        v-if="
          row.price_history &&
            row.price_history.latest &&
            row.price_history.latest.time_created
        "
        class="flex items-end no-wrap"
      >
        <PriceChangeIndicator
          :price-change="row.price_history.latest.amount_change"
        />
        <div
          class="text-text-light q-ml-sm"
          style="font-weight: 400; font-size: 12px; margin-bottom: 1px"
        >
          {{
            $dayjs(row.price_history.latest.time_created, "YYYY-MM-DD").format(
              "ll"
            )
          }}
        </div>
      </div>
      <span v-else>-</span>
    </template>
    <template v-slot:total_price_change="row">
      <PriceChangeIndicator
        v-if="
          row.price_history &&
            row.price_history.total &&
            row.price_history.total.amount_change != 0
        "
        :price-change="row.price_history.total.amount_change"
      />
      <span v-else>-</span>
    </template>

    <template v-slot:view_vehicle>
      <div
        class="flex items-center no-wrap cursor-pointer --hover-lighten --text-sm"
      >
        <div>View Vehicle</div>
        <q-icon name="sym_r_chevron_right" size="18px" class="q-ml-xs" />
      </div>
    </template>

    <template v-slot:below-table>
      <tr v-if="loading && listings.length > 0 && listings.length >= perPage">
        <td colspan="100%">
          <div class="column q-gutter-y-md q-mt-none" style="width: 100%">
            <q-skeleton height="110px" width="100%" animation="fade" />
            <q-skeleton height="110px" width="100%" animation="fade" />
          </div>
        </td>
      </tr>
      <InfiniteScrollObserver
        v-if="!disableInfiniteScroll && listings.length > 0 && !disableSeeMore"
        @intersect="addToQueue(() => getVehicles())"
      />
      <div
        v-if="!loading && listings.length >= perPage && !disableSeeMore"
        style="height: 20px"
      ></div>
    </template>
  </DataTable>
</template>

<script>
import DataTable from "@/components/UI/DataTable.vue";
import VehicleListings from "@/mixins/vehicleListings";
import InfiniteScrollObserver from "@/components/UI/InfiniteScrollObserver.vue";
import ImageCarousel from "@/components/UI/ImageCarousel.vue";
import VehicleWatchlistButton from "@/components/VehicleWatchlistButton.vue";
import ScoreBadge from "@/components/UI/ScoreBadge.vue";
import PriceChangeIndicator from "@/components/UI/PriceChangeIndicator.vue";
import { Dialog } from "quasar";
import ListingDialog from "@/components/ListingDialog.vue";
import PriceDropLabel from "@/components/UI/PriceDropLabel.vue";

export default {
  name: "VehicleListingsTable",
  mixins: [VehicleListings],
  components: {
    PriceDropLabel,
    PriceChangeIndicator,
    ScoreBadge,
    VehicleWatchlistButton,
    ImageCarousel,
    InfiniteScrollObserver,
    DataTable
  },
  props: {
    columns: {
      type: Array,
      default: () => []
    },
    defaultSortField: {
      type: String,
      default: "price"
    },
    defaultSortDirection: {
      type: String,
      default: "asc"
    },
    searchable: {
      type: Boolean,
      default: false
    }
  },
  data: function() {
    return {
      eventQueue: [],
      attachedListeners: new Set(),
      tableColumns: this.columns
    };
  },
  computed: {},
  watch: {
    listings() {
      this.$nextTick(() => {
        this.attachListenersToRows();
      });
    },
    columns(val) {
      this.setTableColumns(val);
    }
  },
  created() {
    this.setTableColumns(this.tableColumns);
  },
  mounted() {
    this.getVehicles();
  },
  methods: {
    setTableColumns(columns) {
      if (columns.length === 0) {
        this.tableColumns = [
          {
            name: "vehicle_overview",
            label: "Vehicle",
            field: "",
            sortable: false
          },
          {
            name: "price_with_drop",
            label: "Price",
            field: "price",
            hideField: true,
            sortable: false
          },
          {
            name: "mileage",
            label: "Mileage",
            field: "mileage",
            format: val => this.formatNumber(val) + " mi",
            sortable: false
          },
          {
            name: "deal_score",
            label: "Deal Score",
            field: "deal_score",
            sortable: false,
            hideField: true
          },
          {
            name: "distance",
            label: "Distance",
            field: "distance",
            format: val => this.formatNumber(val) + " mi away",
            sortable: false
          },
          {
            name: "dealer_name",
            label: "Dealer Name",
            field: "dealer_name",
            format: val => (val.length > 30 ? val.slice(0, 30) + "..." : val),
            sortable: false
          },
          {
            name: "view_vehicle",
            label: "",
            field: "",
            sortable: false
          }
        ];
      } else {
        this.tableColumns = columns;
      }
    },
    addToQueue(event) {
      // Make sure we dont send the same event twice
      if (this.eventQueue.length === 0) {
        this.eventQueue.push(event);
        this.processQueue();
      }
    },
    processQueue() {
      if (this.eventQueue.length > 0 && !this.isProcessingQueue) {
        this.isProcessingQueue = true;
        const event = this.eventQueue.shift();
        event();
        setTimeout(() => {
          this.isProcessingQueue = false;
          this.processQueue();
        }, 750);
      }
    },
    attachListenersToRows() {
      const tableElement = this.$refs.dataTable?.$el;
      if (!tableElement) return;

      this.listings.forEach(listing => {
        if (!this.attachedListeners.has(listing.product_id)) {
          const rowElement = tableElement.querySelector(
            `tr.datatable-row[data-row-key="${listing.product_id}"]`
          );
          if (rowElement) {
            this.attachContentViewListeners(rowElement, {
              advertiser_account_id: listing.advertiser_account_id,
              event_value: listing.bid_value,
              product_id: listing.product_id
            });
            this.attachedListeners.add(listing.product_id);
          }
        }
      });
    },
    promptVehicleListingDialog(product_id) {
      Dialog.create({
        component: ListingDialog,
        componentProps: {
          productId: product_id
        }
      });
    }
  }
};
</script>

<style lang="scss">
.q-table tbody td {
  font-size: 15px;
  font-weight: 600;
}
.q-table thead th {
  font-weight: 600;
  color: color(text);
  font-size: 14px;
}
</style>
